import { notification } from 'antd';
import axios from 'axios';
import { config } from 'config/config';
import apiPaths from 'services/apiPaths';
import { logout } from 'src/actions/authActions';
import { ChallengeTypeEnum } from 'src/shared/enums';
import {
  ICouponParams,
  IScanQrRequest,
  IProcessCheckoutPaymentRequest,
  ISessionCheckoutPaymentRequest,
  IWaitingCheckoutPaymentRequest,
  IJoinCampaignRequest,
  IPreviewDiscoveryCampaign,
  IPregnancyModel,
} from 'src/shared/models';
import { store } from 'src/store';
import { authTypes } from 'src/types/authTypes';
import { getCookie, removeCookieFromClient, setCookieFromClient } from 'utils';

const { API, COOKIES } = config;
const { BASE_URL } = API;
const { ACCESS_TOKEN, REFRESH_TOKEN } = COOKIES;

const baseAxios = axios.create({ baseURL: BASE_URL });
const secureAxios = axios.create({
  baseURL: BASE_URL,
});

baseAxios.defaults.headers.post['Content-Type'] = 'application/json';
secureAxios.defaults.headers.post['Content-Type'] = 'application/json';

const forceLogout = () => {
  store.dispatch(logout);
  removeCookieFromClient(ACCESS_TOKEN);
  removeCookieFromClient(REFRESH_TOKEN);
};

const refreshCall = async () => {
  const accessToken = getCookie(ACCESS_TOKEN, null);
  const refreshToken = getCookie(REFRESH_TOKEN, null);

  if (!accessToken && !refreshToken) return null;

  const data = { accessToken, refreshToken };
  const url = `${BASE_URL}${apiPaths.AUTH.REFRESH}`;

  const refreshCall = await secureAxios.post(url, data, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });

  return refreshCall;
};

export const getRequestHeaders = (accessToken: any) =>
  !accessToken
    ? {}
    : {
        common: {
          Authorization:
            accessToken.indexOf('Bearer') < 0
              ? `Bearer ${accessToken}`
              : accessToken,
          'Content-Type': 'application/json',
        },
      };

secureAxios.interceptors.request.use((req: any) => {
  const token: any = getCookie(ACCESS_TOKEN, req);

  return {
    ...req,
    headers: getRequestHeaders(token),
  };
});

interface IRefreshTokenPool {
  requestCount: number;
  promise?: any;
}
const refreshTokenPool: IRefreshTokenPool = {
  requestCount: 0,
};

secureAxios.interceptors.response.use(
  (res: any) => {
    return res;
  },
  async (error: any) => {
    const { status } = error.response;
    let response;
    if (status === 401) {
      try {
        if (!refreshTokenPool.promise) {
          response = await refreshCall();
          refreshTokenPool.promise = response;
          refreshTokenPool.requestCount = 1;
        } else {
          refreshTokenPool.requestCount++;
        }

        if (response === null || response?.data === null) return;

        const { accessToken, refreshToken } = response.data;
        refreshTokenPool.requestCount--;
        if (refreshTokenPool.requestCount === 0) {
          refreshTokenPool.promise = null;
          store.dispatch({
            type: authTypes.AUTH_SET_TOKENS,
            payload: { accessToken, refreshToken },
          });
          setCookieFromClient(ACCESS_TOKEN, accessToken);
          setCookieFromClient(REFRESH_TOKEN, refreshToken);
        }
        return secureAxios({
          ...error.response.config,
          retry: true,
          headers: getRequestHeaders(accessToken),
        });
      } catch (err) {
        console.error(err);
        notification.info({
          message: 'Session expired',
        });

        forceLogout();
        refreshTokenPool.requestCount = 0;
        refreshTokenPool.promise = null;

        throw err;
      }
    }
    throw error;
  },
);

export const loginCall = async ({
  dataPath = apiPaths.AUTH.LOGIN,
  data,
}: {
  dataPath?: string;
  data: any;
}) => {
  const url = `${BASE_URL}${dataPath}`;

  const headers = new Headers();
  headers.append('Content-Type', 'application/json');
  headers.append('Accept', 'application/json');

  const response = await fetch(url, {
    body: JSON.stringify(data),
    method: 'POST',
    credentials: 'include',
    headers: headers,
  });

  return await response.json();
};

export const logoutCall = ({
  dataPath = apiPaths.AUTH.LOGOUT,
}: {
  dataPath: string;
}) => secureAxios.post(dataPath, {});

export const registerCall = ({
  dataPath = apiPaths.AUTH.REGISTER,
  data,
}: {
  dataPath?: string;
  data: any;
}) =>
  baseAxios.post(dataPath, data, {
    headers: {
      'Content-Type': 'application/json',
    },
  });

//TODO - Parametrizar estas llamadas
export const getUserInfo = ({
  dataPath = apiPaths.CALL.USER_INFO,
  callConfig = {},
}: {
  dataPath?: string;
  callConfig?: {};
}) => secureAxios.get(dataPath, callConfig);

export const getUserTemporalData = ({
  dataPath,
  callConfig,
}: {
  dataPath?: string;
  callConfig?: {};
}) => secureAxios.get(dataPath, callConfig);

export const getMCInfo = (email: string) => {
  let dataPath = `${apiPaths.MC_NEWSLETTER.INFO}?email=${email}`;
  return getDataCall({ dataPath, callConfig: {} });
};

export const updateUserInfo = ({
  dataPath = apiPaths.PROFILE.UPDATE_USER_INFO,
  data,
}: {
  dataPath?: string;
  data: any;
}) => secureAxios.put(dataPath, data);

export const changePassword = ({
  dataPath = apiPaths.RESET_PASSWORD,
  data,
  callConfig = {},
}: {
  dataPath?: string;
  data: any;
  callConfig?: {};
}) => baseAxios.post(dataPath, data, callConfig);

export const getRecoveryPasswordUserInfo = ({
  dataPath = apiPaths.RECOVERY_PASSWORD_USERINFO,
  data,
  callConfig = {},
}: {
  dataPath?: string;
  data: any;
  callConfig?: {};
}) => baseAxios.post(dataPath, data, callConfig);

export const recoveryPassword = ({
  dataPath = apiPaths.RECOVERY_PASSWORD,
  callConfig = {},
}: {
  dataPath?: string;
  callConfig?: {};
}) => baseAxios.get(dataPath, callConfig);

export const getSpecialties = ({
  dataPath = apiPaths.CALL.SPECIALTY_LIST,
  callConfig = {},
}: {
  dataPath?: string;
  callConfig?: {};
}) => baseAxios.get(dataPath, callConfig);

export const getExperienceTags = ({
  dataPath = apiPaths.CHALLENGES.GET_TAGS_EXPERIENCES,
  callConfig = {},
}: {
  dataPath?: string;
  callConfig?: {};
}) => baseAxios.get(dataPath, callConfig);

export const getChallengeTags = ({
  dataPath = apiPaths.CHALLENGES.GET_TAGS_CHALLENGES,
  callConfig = {},
}: {
  dataPath?: string;
  callConfig?: {};
}) => baseAxios.get(dataPath, callConfig);

export const getChallengesByUser = ({
  dataPath = apiPaths.CHALLENGES.GET_EVENTS,
  callConfig = {},
}: {
  dataPath?: string;
  callConfig?: {};
}) => secureAxios.get(dataPath, callConfig);

export const challengeUserInscription = ({
  dataPath = apiPaths.CHALLENGES.SUBSCRIBE_USER,
  callConfig,
}: {
  dataPath?: string;
  callConfig?: {};
}) => secureAxios.put(dataPath, callConfig);

export const completeSurvey = ({
  dataPath = apiPaths.CALL.COMPLETE_SURVEY,
  data,
  callConfig = {},
}: {
  dataPath?: string;
  data: any;
  callConfig?: {};
}) => secureAxios.post(dataPath, data, callConfig);

export const completeChallengeSurvey = ({
  dataPath = apiPaths.CALL.COMPLETE_CHALLENGE_SURVEY,
  data,
  callConfig = {},
}: {
  dataPath?: string;
  data: any;
  callConfig?: {};
}) => secureAxios.post(dataPath, data, callConfig);

export const changeRecoveryPassword = ({
  dataPath = apiPaths.RECOVERY_PASSWORD,
  data,
  callConfig = {},
}: {
  dataPath?: string;
  data: any;
  callConfig?: {};
}) => baseAxios.post(dataPath, data, callConfig);

export const uploadImage = ({
  dataPath = apiPaths.UPLOAD_IMAGE,
  data,
  callConfig = {},
}: {
  dataPath?: string;
  data: any;
  callConfig?: {};
}) => secureAxios.post(dataPath, data, callConfig);

export const getUserPoints = ({
  dataPath = apiPaths.MAGENTO.GET_CUSTOMER_POINTS,
  callConfig,
}: {
  dataPath?: string;
  callConfig?: {};
}) => secureAxios.get(dataPath, callConfig);

export const getCategoriesByChallengeType = (
  challengeType: ChallengeTypeEnum,
  callConfig: any,
) =>
  getDataCall({
    dataPath: `${apiPaths.CHALLENGES.GET_CATEGORIES}/${challengeType}`,
    callConfig,
  });

export const getApiCombo = (idCombo: string, param?: string | number) => {
  let dataPath = `${apiPaths.COMBOS}?id=${idCombo}`;
  if (param) dataPath += `&param=${param}`;

  return getDataCall({
    dataPath,
    callConfig: {},
  });
};

export const getPublicApiCombo = (idCombo: string, param?: string | number) => {
  let dataPath = `${apiPaths.COMBOS_PUBLIC}?id=${idCombo}`;
  if (param) dataPath += `&param=${param}`;

  return getPublicDataCall({
    dataPath,
    callConfig: {},
  });
};

export const getDataCall = ({ dataPath, callConfig = {} }) =>
  secureAxios.get(dataPath, callConfig);

export const getPublicDataCall = ({ dataPath, callConfig }) =>
  baseAxios.get(dataPath, callConfig);

export const getDataByIdCall = ({ dataPath, id, callConfig }) =>
  secureAxios.get(`${dataPath}/${id}`, callConfig);

export const postDataCall = ({ dataPath, data, callConfig }) =>
  secureAxios.post(dataPath, data, callConfig);

export const getEmailTest = (id: number) => {
  let dataPath = `${apiPaths.PREVIEW_EMAIL}`;
  getDataByIdCall({ dataPath, id, callConfig: {} });
};

export const postDataCallById = ({
  dataPath,
  id,
  data,
}: {
  dataPath: string;
  id: number;
  data?: { [key: string]: any };
}) => secureAxios.post(`${dataPath}/${id}`, data);
export const putDataCall = ({ dataPath, data, callConfig }) =>
  secureAxios.put(dataPath, data, callConfig);

export const putDataCallById = ({
  dataPath,
  id,
  data,
}: {
  dataPath: string;
  id: number;
  data?: { [key: string]: any };
}) => secureAxios.put(`${dataPath}/${id}`, data);

export const getAccountStatus = ({
  email,
  dermo,
}: {
  email: string;
  dermo: boolean;
}) => baseAxios.post(apiPaths.AUTH.STATUS, { email, dermo });

export const deleteDataCall = ({ dataPath, id, callConfig }) =>
  secureAxios.delete(`${dataPath}/${id}`, callConfig);

export const getQrInfo = ({
  dataPath,
  callConfig = {},
}: {
  dataPath?: string;
  callConfig?: {};
}) => {
  const path = `${BASE_URL}${apiPaths.QR_BRIDGE.GET_QR_INFO}?${dataPath}`;
  return baseAxios.get(path, callConfig);
};

export const getProductInfo = ({
  dataPath,
  callConfig = {},
}: {
  dataPath?: string;
  callConfig?: {};
}) => {
  const path = `${BASE_URL}${apiPaths.QR_PRODUCTS.GET_DETAIL}?${dataPath}`;
  return baseAxios.get(path, callConfig);
};

export const postAxiosCall = ({
  dataPath,
  data,
  callConfig = {},
}: {
  dataPath: string;
  data: any;
  callConfig?: {};
}) => baseAxios.post(dataPath, data, callConfig);

export const assignQrFromRegister = async ({
  scanId,
  apiKey,
  qrCode,
  scanUserId,
  email,
  password,
}: {
  scanId: string;
  apiKey: string;
  qrCode: string;
  scanUserId: string;
  email: string;
  password: string;
}) => {
  try {
    const response = await loginCall({
      dataPath: apiPaths.AUTH.LOGIN,
      data: {
        email,
        password,
      },
    });
    const { accessToken } = response.data;
    const headers = getRequestHeaders(accessToken);

    const registerQrAxios = axios.create({ baseURL: BASE_URL });
    registerQrAxios.defaults.headers.common['Authorization'] =
      accessToken.indexOf('Bearer') < 0 ? `Bearer ${accessToken}` : accessToken;
    registerQrAxios.defaults.headers.common['Content-Type'] =
      'application/json';

    return registerQrAxios.post(apiPaths.AUTH.ASSIGN_QR, {
      scanId,
      apiKey,
      qrCode,
      scanUserId,
    });
  } catch (error) {
    console.group('error', error);
    throw error;
  }
};

export const getCheckoutPharmacies = (
  idChallenge: string | number,
  zipCode: string | number,
) => {
  const dataPath = `${apiPaths.CHALLENGES.CHECKOUT_PHARMACIES}/${idChallenge}?zipcode=${zipCode}`;
  return getDataCall({ dataPath, callConfig: {} });
};

export const assignQR = async ({
  scanId,
  apiKey,
  qrCode,
  scanUserId,
}: {
  scanId: string;
  apiKey: string;
  qrCode: string;
  scanUserId: string;
}) => {
  try {
    const response = await postDataCall({
      dataPath: apiPaths.AUTH.ASSIGN_QR,
      data: {
        scanId,
        apiKey,
        qrCode,
        scanUserId,
      },
      callConfig: {},
    });
    return response.data;
  } catch (err) {
    throw err;
  }
};
export const checkUserQrs = async ({
  scanId,
  apiKey,
  qrCode,
  scanUserId,
}: {
  scanId: string;
  apiKey: string;
  qrCode: string;
  scanUserId: string;
}) => {
  try {
    const response = await postDataCall({
      dataPath: apiPaths.AUTH.CHECK_USER_QRS,
      data: {
        scanId,
        apiKey,
        qrCode,
        scanUserId,
      },
      callConfig: {},
    });
    return response.data;
  } catch (err) {
    throw err;
  }
};

export const getDermoData = ({
  dataPath = apiPaths.DERMO.GET_DERMO_DATA,
  callConfig,
}: {
  dataPath?: string;
  callConfig?: {};
}) => secureAxios.get(dataPath, callConfig);

export const getDermoUserPos = ({
  dataPath = apiPaths.DERMO.GET_DERMO_USER_POS,
  callConfig,
}: {
  dataPath?: string;
  callConfig?: {};
}) => secureAxios.get(dataPath, callConfig);

export const dermoUserPos = async ({
  idDermoCenter,
  idDermoRegion,
  idDermoPos,
  idDermo,
}: {
  idDermoCenter: number;
  idDermoRegion: number;
  idDermoPos: number;
  idDermo: string;
}) => {
  try {
    const response = await postDataCall({
      dataPath: apiPaths.DERMO.REGISTER_DERMO,
      data: {
        idDermoCenter,
        idDermoRegion,
        idDermoPos,
        idDermo,
      },
      callConfig: {},
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getUserDermoRole = async ({
  dataPath = `${apiPaths.DERMO.USER_ROLES}${apiPaths.DERMO.GET_DERMO_USER_ROLE}`,
  callConfig,
}: {
  dataPath?: string;
  callConfig?: {};
}) => secureAxios.get(dataPath, callConfig);

export const userRoles = async ({
  idRoleType,
  idSpecialty,
  userStatus,
}: {
  idRoleType: number;
  idSpecialty: number;
  userStatus: number;
}) => {
  try {
    const response = await postDataCall({
      dataPath: apiPaths.DERMO.USER_ROLES,
      data: {
        idRoleType,
        idSpecialty,
        userStatus,
      },
      callConfig: {},
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const setUserStatus = async ({
  idRoleType,
  userStatus,
}: {
  idRoleType: number;
  userStatus: number;
}) => {
  try {
    const response = await putDataCall({
      dataPath: `${apiPaths.DERMO.USER_ROLES}${apiPaths.DERMO.SET_DERMO_USER_STATUS}`,
      data: {
        idRoleType,
        userStatus,
      },
      callConfig: {},
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const setDermoOnboarding = async (dermoOnboarding: boolean) => {
  try {
    const response = await putDataCall({
      dataPath: apiPaths.PROFILE.UPDATE_USER_DERMO_ONBOARDING,
      data: { dermoOnboarding },
      callConfig: {},
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const dermoSoftDelete = async (idUserRole: number) => {
  try {
    const response = await deleteDataCall({
      dataPath: apiPaths.DERMO.DERMO_SOFT_DELETE,
      id: idUserRole,
      callConfig: {},
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getDermoFormations = async ({
  dataPath = `${apiPaths.CHALLENGES.GET_FORMATIONS}`,
  callConfig,
}: {
  dataPath?: string;
  callConfig?: {};
}) => secureAxios.get(dataPath, callConfig);

export const getProductLandingById = async (consumableId: number) => {
  const dataPath = `${apiPaths.CONSUMABLES.GET_PRODUCT_LANDING}/${consumableId}`;
  const callConfig = {};

  return secureAxios.get(dataPath, callConfig);
};

export const consumeBonus = async (
  couponData: ICouponParams,
  newCoupon: ICouponParams,
) => {
  try {
    const response = await putDataCall({
      dataPath: apiPaths.CONSUMABLES.CONSUME_BONUS,
      data: {
        couponId: couponData.id,
        newCouponId: newCoupon.id,
      },
      callConfig: {},
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getAllCustomerCoupons = async () => {
  const dataPath = apiPaths.CONSUMABLES.GET_ALL_CUSTOMER_COUPONS;
  const callConfig = {};

  return secureAxios.get(dataPath, callConfig);
};

export const scanQr = async (scanQrRequest: IScanQrRequest) => {
  const dataPath = apiPaths.CONSUMABLES.SCAN_QR;
  const callConfig = {};

  return secureAxios.post(dataPath, scanQrRequest, callConfig);
};

export const previewScanQr = async (scanQrRequest: IScanQrRequest) => {
  const dataPath = apiPaths.CONSUMABLES.PREVIEW_SCAN_QR;
  const callConfig = {};

  return baseAxios.post(dataPath, scanQrRequest, callConfig);
};

export const getQrLanding = async (
  apiKey: string | string[],
  qrCode: string | string[],
  scanId: string | string[],
  scanUserId: string | string[],
) => {
  const dataPath = `${apiPaths.CONSUMABLES.GET_QR_LANDING_QR_PRODUCT}?k=${apiKey}&q=${qrCode}&s=${scanId}&u=${scanUserId}`;
  const callConfig = {};
  return secureAxios.get(dataPath, callConfig);
};

export const updateUserRole = async () => {
  const dataPath = apiPaths.AUTH.UPDATE_USER_ROLE;

  return secureAxios.put(dataPath);
};

export const createAdyenCheckout = async (
  data: ISessionCheckoutPaymentRequest,
) => {
  try {
    const response = await postDataCall({
      dataPath: apiPaths.CHALLENGES.CREATE_CHECKOUT_PAYMENT,
      data: data,
      callConfig: {},
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const processAdyenCheckout = async (
  data: IProcessCheckoutPaymentRequest,
) => {
  try {
    const response = await postDataCall({
      dataPath: apiPaths.CHALLENGES.PROCESS_CHECKOUT_PAYMENT,
      data: data,
      callConfig: {},
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const waitingAdyenCheckout = async (
  data: IWaitingCheckoutPaymentRequest,
) => {
  try {
    const response = await postDataCall({
      dataPath: apiPaths.CHALLENGES.WAITING_CHECKOUT_PAYMENT,
      data: data,
      callConfig: {},
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const previewDiscoveryCampaign = async (
  data: IPreviewDiscoveryCampaign,
) => {
  try {
    const response = await postDataCall({
      dataPath: apiPaths.CONSUMABLES.PREVIEW_DISCOVERY_CAMPAIGN,
      data: data,
      callConfig: {},
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const joinCampaign = async (data: IJoinCampaignRequest) => {
  try {
    const response = await postDataCall({
      dataPath: apiPaths.CONSUMABLES.JOIN_CAMPAIGN,
      data: data,
      callConfig: {},
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getUserActiveCampaigns = async () => {
  const dataPath = apiPaths.CONSUMABLES.GET_ACTIVE_CAMPAIGNS;
  return secureAxios.get(dataPath, {});
};

export const assignPregnancyRole = async () => {
  try {
    const response = await postDataCall({
      dataPath: apiPaths.PREGNANCY.ASSIGN_PREGNANCY_ROLE,
      data: {},
      callConfig: {},
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getExpirationParameters = async () => {
  const dataPath = apiPaths.COMBO_IDS.EXPIRATION_PARAMS;

  try {
    const expPointsParams = await getApiCombo(dataPath);
    return expPointsParams;
  } catch (error) {
    throw error;
  }
};

export const getUserPointsToExpire = async () => {
  const dataPath = apiPaths.POINTS_API.GET_EXPIRATION_POINTS;
  let expirationPointsParams;

  try {
    expirationPointsParams = await getExpirationParameters();
  } catch (error) {
    throw error;
  }

  const expirationDate = expirationPointsParams.data.find(
    (param) => param.name === 'date_of_expiration_points',
  )?.value;

  if (!expirationDate) {
    throw new Error('No date of expiration points found');
  }

  const expirationAndBalace = await secureAxios.get(dataPath, {
    params: {
      expirationDate: expirationDate,
    },
  });

  return { ...expirationAndBalace.data, expirationDate };
};
